import React, { useState } from 'react';
import { X, Rocket, Shield } from 'lucide-react';
import { createHuntItem } from '../services/firestore';
import { moderateContent } from '../services/moderation';

const ICONS = [
  '🚀', '🎲', '🧦', '🍺', '🚫', '💡', '🦄', '🤖', '🌟', '⚡',
  '🍕', '🌮', '🍔', '🍟', '🍩', '🌭', '🥓', '🧀', '🍖', '🥩',
  '🐔', '🐷', '🐮', '🐶', '🐱', '🐭', '🐹', '🐰', '🦊', '🐻',
  '🐼', '🐨', '🐯', '🦁', '🐸', '🐵', '🙈', '🙉', '🙊', '🐒',
  '🦖', '🦕', '🐉', '🦄', '🐝', '🐛', '🦋', '🐌', '🐞', '🦗',
  '💩', '👻', '🤡', '👽', '🤖', '🎃', '😈', '👹', '👺', '💀',
  '☠️', '🔥', '💣', '💥', '⚡', '🌪️', '🌊', '🎉', '🎊', '🎈',
  '🏆', '🥇', '🥈', '🥉', '🏅', '🎖️', '🎗️', '🏵️', '🎭', '🎨',
  '🎪', '🎬', '🎤', '🎧', '🎼', '🎹', '🥁', '🎺', '🎸', '🪕',
  '🎰', '🎮', '🕹️', '🎲', '🧩', '🎯', '🎳', '🎱', '🏀', '⚽'
];

const COLORS = [
  'from-pink-500 to-orange-400',
  'from-purple-500 to-pink-500',
  'from-blue-400 to-cyan-500',
  'from-yellow-400 to-amber-600',
  'from-green-400 to-emerald-600',
  'from-red-500 to-pink-500',
];

export default function AddHuntModal({ isOpen, onClose, user, onSuccess, initialTitle = '', initialDesc = '' }) {
  const [title, setTitle] = useState(initialTitle);
  const [desc, setDesc] = useState(initialDesc);
  const [selectedIcon, setSelectedIcon] = useState(ICONS[0]);
  const [selectedColor, setSelectedColor] = useState(COLORS[0]);
  const [tags, setTags] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAllIcons, setShowAllIcons] = useState(false);

  // Update form fields when initial values change
  React.useEffect(() => {
    if (isOpen) {
      setTitle(initialTitle);
      setDesc(initialDesc);
    }
  }, [isOpen, initialTitle, initialDesc]);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      setError('You must be logged in to post');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Run AI moderation
      const modResult = await moderateContent(title, desc, 'startup idea');

      if (modResult.decision === 'REJECT') {
        setError(`⚠️ Content not allowed: ${modResult.reason}. AlcoFounder is for humorous startup ideas and stories, not marketing or spam.`);
        setLoading(false);
        return;
      }

      const tagsArray = tags.split(',').map(t => t.trim()).filter(Boolean);

      await createHuntItem({
        title,
        desc,
        icon: selectedIcon,
        color: selectedColor,
        tags: tagsArray,
        author: user.displayName || user.email
      }, user.uid);

      onSuccess?.();
      onClose();

      // Reset form
      setTitle('');
      setDesc('');
      setTags('');
      setSelectedIcon(ICONS[0]);
      setSelectedColor(COLORS[0]);
    } catch (err) {
      setError(err.message || 'Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-slate-900 rounded-2xl p-8 max-w-2xl w-full border border-slate-800 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Rocket className="text-amber-500" />
            Post Your Drunk Idea
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-bold text-slate-300 mb-2">Title*</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Uber for Pigeons"
              className="w-full bg-slate-800 text-white px-4 py-3 rounded-lg border border-slate-700 focus:border-amber-500 focus:outline-none"
              required
              maxLength={100}
            />
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-300 mb-2">Description*</label>
            <textarea
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Describe your brilliant idea..."
              className="w-full bg-slate-800 text-white px-4 py-3 rounded-lg border border-slate-700 focus:border-amber-500 focus:outline-none h-24"
              required
              maxLength={300}
            />
            <div className="text-xs text-slate-500 mt-1">{desc.length}/300</div>
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-300 mb-2">Choose Icon</label>
            <div className="flex flex-wrap gap-2">
              {(showAllIcons ? ICONS : ICONS.slice(0, 20)).map(icon => (
                <button
                  key={icon}
                  type="button"
                  onClick={() => setSelectedIcon(icon)}
                  className={`text-3xl p-3 rounded-lg transition ${
                    selectedIcon === icon
                      ? 'bg-amber-500/20 ring-2 ring-amber-500'
                      : 'bg-slate-800 hover:bg-slate-700'
                  }`}
                >
                  {icon}
                </button>
              ))}
              {!showAllIcons && (
                <button
                  type="button"
                  onClick={() => setShowAllIcons(true)}
                  className="text-sm px-4 py-3 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition font-medium"
                >
                  More...
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-300 mb-2">Choose Color</label>
            <div className="flex flex-wrap gap-2">
              {COLORS.map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setSelectedColor(color)}
                  className={`w-12 h-12 rounded-lg bg-gradient-to-br ${color} transition ${
                    selectedColor === color
                      ? 'ring-2 ring-white ring-offset-2 ring-offset-slate-900'
                      : ''
                  }`}
                />
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-bold text-slate-300 mb-2">Tags (comma separated)</label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="e.g., AI, Hardware, Blockchain"
              className="w-full bg-slate-800 text-white px-4 py-3 rounded-lg border border-slate-700 focus:border-amber-500 focus:outline-none"
            />
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-2 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold py-4 rounded-lg transition disabled:opacity-50"
          >
            {loading ? 'Posting...' : 'Post to Drunk Hunt 🚀'}
          </button>
        </form>
      </div>
    </div>
  );
}
